import { authorizedFetch, useAuth } from '@yeshuman/sdk'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'



export interface AppointmentBooking {
  id: string
  call_record_id: string
  call_outcome_id: string
  provider: string
  created_source: string
  booking_status: string
  sync_status: string
  external_appointment_uid: string
  external_consult_uid: string
  external_contact_id: string
  external_animal_id: string
  external_resource_uid: string
  appointment_type_uid: string
  appointment_type_name: string
  resource_name: string
  start_at: string
  duration_minutes: number
  last_synced_at: string | null
  provider_payload_json: Record<string, unknown>
  provider_snapshot_json: Record<string, unknown>
  created_at: string
  updated_at: string
}

export interface CallOutcome {
  id: string
  call_record_id: string
  outcome_status: string
  disposition: string
  notes: string
  patient_name: string
  contact_name: string
  contact_external_id: string
  animal_external_id: string
  metadata_json: Record<string, unknown>
  created_at: string
  updated_at: string
  latest_booking: AppointmentBooking | null
}

export interface AppointmentContext {
  call_record_id: string
  vapi_call_id: string
  customer_name: string
  customer_number: string
  existing_outcome_id: string | null
  latest_booking_id: string | null
  outcome_status: string
  patient_name: string
  contact_name: string
  latest_booking: AppointmentBooking | null
}

export interface AppointmentType {
  uid: string
  name: string
  default_duration_minutes: number
  bookable: boolean
}

export interface Resource {
  uid: string
  name: string
  ownership_id: string
  separation_name: string
  bookable: boolean
}

export type CallOutcomesListParams = {
  limit?: number
  booking_start_after?: string
  booking_start_before?: string
  order?: 'created' | 'booking_start'
}

export type UseCallOutcomesOptions = {
  refetchInterval?: number | false
  params?: CallOutcomesListParams
}

export function useCallOutcomes(
  limit = 50,
  options?: UseCallOutcomesOptions
) {
  const { token } = useAuth()
  const params = options?.params
  const effectiveLimit = params?.limit ?? limit

  return useQuery({
    queryKey: [
      'call-outcomes',
      effectiveLimit,
      params?.booking_start_after ?? null,
      params?.booking_start_before ?? null,
      params?.order ?? 'created',
    ],
    queryFn: async (): Promise<CallOutcome[]> => {
      if (!token) throw new Error('Not authenticated')
      const search = new URLSearchParams({ limit: String(effectiveLimit) })
      if (params?.booking_start_after) {
        search.set('booking_start_after', params.booking_start_after)
      }
      if (params?.booking_start_before) {
        search.set('booking_start_before', params.booking_start_before)
      }
      if (params?.order) {
        search.set('order', params.order)
      }
      const res = await authorizedFetch(`/api/call-outcomes/?${search}`)
      if (res.status === 403) throw new Error('Forbidden')
      if (!res.ok) throw new Error('Failed to fetch call outcomes')
      return res.json()
    },
    enabled: !!token,
    refetchInterval: options?.refetchInterval ?? false,
  })
}

export function useAppointmentContext(callId: string | undefined) {
  const { token } = useAuth()

  return useQuery({
    queryKey: ['call-outcome-appointment-context', callId],
    queryFn: async (): Promise<AppointmentContext> => {
      if (!token) throw new Error('Not authenticated')
      if (!callId) throw new Error('Missing call id')
      const res = await authorizedFetch(
        `/api/call-outcomes/appointment-context/${callId}`
      )
      if (res.status === 403) throw new Error('Forbidden')
      if (res.status === 404) throw new Error('Not found')
      if (!res.ok) throw new Error('Failed to fetch appointment context')
      return res.json()
    },
    enabled: !!token && !!callId,
  })
}

export function useAppointmentTypes() {
  const { token } = useAuth()

  return useQuery({
    queryKey: ['call-outcome-appointment-types'],
    queryFn: async (): Promise<AppointmentType[]> => {
      if (!token) throw new Error('Not authenticated')
      const res = await authorizedFetch('/api/call-outcomes/appointment-types')
      if (res.status === 403) throw new Error('Forbidden')
      if (!res.ok) throw new Error('Failed to fetch appointment types')
      return res.json()
    },
    enabled: !!token,
  })
}

export function useAppointmentResources() {
  const { token } = useAuth()

  return useQuery({
    queryKey: ['call-outcome-resources'],
    queryFn: async (): Promise<Resource[]> => {
      if (!token) throw new Error('Not authenticated')
      const res = await authorizedFetch('/api/call-outcomes/resources')
      if (res.status === 403) throw new Error('Forbidden')
      if (!res.ok) throw new Error('Failed to fetch appointment resources')
      return res.json()
    },
    enabled: !!token,
  })
}

export interface Contact {
  external_id: string
  name: string
  phone: string
  email: string
}

export interface ContactLookupResult {
  found: boolean
  contacts: Contact[]
  message: string
}

export interface Animal {
  external_id: string
  name: string
  species: string
  contact_external_id: string
}

export interface AvailabilitySlot {
  start_at: string
  duration_minutes: number
  resource_uid: string
  resource_name: string
  appointment_type_uid: string
  appointment_type_name: string
  bookable: boolean
}

export function useContactLookup(phone: string) {
  const { token } = useAuth()
  const trimmed = phone.trim()

  return useQuery({
    queryKey: ['call-outcome-contact-lookup', trimmed],
    queryFn: async (): Promise<ContactLookupResult> => {
      if (!token) throw new Error('Not authenticated')
      const params = new URLSearchParams({ phone: trimmed })
      const res = await authorizedFetch(`/api/call-outcomes/contact-lookup?${params}`)
      if (res.status === 403) throw new Error('Forbidden')
      if (!res.ok) throw new Error('Contact lookup failed')
      return res.json()
    },
    enabled: !!token && trimmed.length > 0,
  })
}

export function useAnimalsForContact(contactExternalId: string) {
  const { token } = useAuth()

  return useQuery({
    queryKey: ['call-outcome-animals', contactExternalId],
    queryFn: async (): Promise<Animal[]> => {
      if (!token) throw new Error('Not authenticated')
      const params = new URLSearchParams({ contact_external_id: contactExternalId })
      const res = await authorizedFetch(`/api/call-outcomes/animals?${params}`)
      if (res.status === 403) throw new Error('Forbidden')
      if (!res.ok) throw new Error('Failed to fetch animals')
      return res.json()
    },
    enabled: !!token && !!contactExternalId,
  })
}

export interface AvailabilityQuery {
  appointment_type_uid: string
  resource_uid: string
  start_at: string
  end_at: string
  duration_minutes: number
}

export function useAppointmentAvailability(query: AvailabilityQuery | null) {
  const { token } = useAuth()

  return useQuery({
    queryKey: ['call-outcome-availability', query],
    queryFn: async (): Promise<AvailabilitySlot[]> => {
      if (!token) throw new Error('Not authenticated')
      const params = new URLSearchParams({
        appointment_type_uid: query!.appointment_type_uid,
        resource_uid: query!.resource_uid,
        start_at: query!.start_at,
        end_at: query!.end_at,
        duration_minutes: String(query!.duration_minutes),
      })
      const res = await authorizedFetch(`/api/call-outcomes/availability?${params}`)
      if (res.status === 403) throw new Error('Forbidden')
      if (!res.ok) throw new Error('Failed to fetch availability')
      return res.json()
    },
    enabled: !!token && !!query,
  })
}

export interface BookAppointmentRequest {
  call_record_id: string
  animal_external_id: string
  contact_external_id: string
  appointment_type_uid: string
  resource_uid: string
  start_at: string
  duration_minutes: number
  patient_name?: string
  contact_name?: string
  notes?: string
  created_source?: string
}

export function useBookAppointment() {
  const { token } = useAuth()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (payload: BookAppointmentRequest): Promise<AppointmentBooking> => {
      if (!token) throw new Error('Not authenticated')
      const res = await authorizedFetch('/api/call-outcomes/book-appointment', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ created_source: 'staff', ...payload }),
      })
      if (!res.ok) {
        let detail = 'Booking failed'
        try {
          const body = await res.json()
          detail = body.detail || body.error || detail
        } catch {
          // keep default
        }
        throw new Error(detail)
      }
      return res.json()
    },
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: ['call-outcomes'] })
      void queryClient.invalidateQueries({
        queryKey: ['call-outcome-appointment-context', variables.call_record_id],
      })
    },
  })
}
